import React, { useState } from 'react';
import AccountFullDTO from 'features/accounts/dtos/AccountFullDTO';
import PageCard from 'features/shared/components/card/PageCard';
import AccountRole from 'features/accounts/components/AccountRole';
import NoContentWrapper from 'features/shared/components/noContentWrapper/NoContentWrapper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accounts_DeleteRole, Accounts_InsertRole } from 'data/endpoints';
import { trackPromise } from 'react-promise-tracker';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import IRepository from 'data/IRepository';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import AccountRoleDTO from 'features/accounts/dtos/AccountRoleDTO';
import AccountRoleDialog from 'features/accounts/components/accountRoleDialog/AccountRoleDialog';
import { AccountRoles_AccountRoleDialog } from 'features/shared/components/loaders/promiseLoadingAreas';
import { CustomerRole } from 'features/customer/enums/customerRole';
import ContentDialogConfirm from 'features/shared/components/contentDialog/ContentDialogConfirm';
import IContentDialogService from 'features/shared/components/contentDialog/IContentDialogService';
import CardHeaderButton from 'features/shared/components/card/CardHeaderButton';
import { useTranslation } from 'react-i18next';
import { getForAdminParam } from 'features/shared/helpers/queryParams';

interface Props {
  account: AccountFullDTO;
  accountModified: number;
  setAccountModified: React.Dispatch<React.SetStateAction<number>>;
  forAdminUse?: boolean;
}

const AccountRolesSection = ({ account, accountModified, setAccountModified, forAdminUse = false  }: Props) => {
  // translations
  const tLabels = useTranslation("accounts\\accounts").t;

  // DI
  const rolesRepository = useInjection<IRepository<AccountFullDTO>>(IocTypes.Repository); 
  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService); 

  // state
  const [showAccountRoleDialog, setShowAccountRoleDialog] = useState(false);

  // helpers
  const showAddingRoles = (): boolean => {
    if (account.roles) {
      return account.roles.length != Object.keys(CustomerRole).length - 1; // - 1 => Undenfined
    }

    return true;
  }
  
  // handlers
  const deleteRolenHandler = async (role: AccountRoleDTO) => {
    let response = await trackPromise(
      rolesRepository.delete({
        url: Accounts_DeleteRole,
        routeParams: getRouteParams("accountRoleId", role.accountRoleId!),
        params: getForAdminParam(forAdminUse)
      })
    );

    if (response.status == 204) {
      setAccountModified(accountModified + 1);
    }
  }

  const insertAccountRoleHandler = async (role: AccountRoleDTO) => {
    role.accountId = account.accountId;
    
    let response = await trackPromise(
      rolesRepository.post<string>({
        url: Accounts_InsertRole,
        data: role,
        routeParams: getRouteParams("accountId", account.accountId!),
        params: getForAdminParam(forAdminUse)
      }),
      AccountRoles_AccountRoleDialog
    );

    if (response.status == 200) {
      setShowAccountRoleDialog(false);
      setAccountModified(accountModified + 1);
    }
  }
  
  const confirmDeleteRoleHandler = (role: AccountRoleDTO) => {
    dialogService.openDialog(tLabels("accountRoleSection.deleteRole"), () => {
      return (
        <ContentDialogConfirm
          message={tLabels("accountRoleSection.deleteRoleQuestion")}
          action={() => deleteRolenHandler(role)}
        />
      );
    });
  }

  // sub-renders
  const renderRoles = () => {
    return account.roles?.map((item) =>
      <div className="py-1" key={item.accountRoleId}>
        <AccountRole 
          role={item} 
          onDeleteClick={confirmDeleteRoleHandler}
        />
      </div>
    );
  }
  
  const renderActionBtns = () => {
    let elem = (
      <CardHeaderButton
        type="button"
        title={tLabels("accountRoleSection.addRole")}
        onClick={() => setShowAccountRoleDialog(true)}
        key={1}
      >
        <FontAwesomeIcon icon="plus" />
      </CardHeaderButton>
    );

    return [elem];
  };

  // render
  return (
    <PageCard 
      title={tLabels("accountRoleSection.manageRole")}
      headerActionBtns={showAddingRoles() ? renderActionBtns : undefined}
    >
      <NoContentWrapper
        data={account.roles}
        loadStatus="AfterFirstLoad"
        noContentRender={() => <div className="text-muted">{tLabels("accountRoleSection.noRoleAdded")}</div>}
      >
        {renderRoles()} 
      </NoContentWrapper>

      <AccountRoleDialog 
        show={showAccountRoleDialog}
        disabledRoles={account.roles}
        setShow={setShowAccountRoleDialog}
        onSubmit={insertAccountRoleHandler}
      />
    </PageCard>
  )
}

export default AccountRolesSection;
